import { useEffect, useRef, useState } from 'preact/hooks';
import { FindForm } from './FindForm';
import { ApiError, fetchBooking } from '../lib/api';
import { travelPage } from '../content/wedding';

/**
 * The way in to the room block, and the door in front of it.
 *
 * The booking link is never in this bundle and never in the page. The button
 * below asks the Worker for it, and the Worker only answers a request carrying
 * a party pass — so a crawler reading the travel page finds a button that
 * posts nowhere, and the block cannot be emptied by anything that does not
 * know a guest's name and postcode. The reasoning in full is on `getBooking`
 * in `apps/api/src/index.ts`.
 *
 * What that costs a guest is one dialog, once: the pass lasts long enough that
 * anyone who came here from replying is already through it, and anyone who was
 * not is asked for the same two fields the RSVP page asks for.
 *
 * A native `<dialog>` rather than a div with a class on it — `showModal`
 * brings the focus trap, the Escape key, the inert background and the
 * backdrop with it, and none of that is worth reimplementing badly.
 */

/**
 * What the dialog is showing:
 *
 *   checking  asking the Worker, which is also how we find out whether this
 *             guest already holds a pass — one request rather than a probe on
 *             every page load
 *   asking    no pass: the name box
 *   leaving   matched: the browser is already on its way to the hotel
 *   soon      matched, but the hotel has not sent us the link yet
 *   error     something else went wrong, in the Worker's own words
 */
type Stage = 'checking' | 'asking' | 'leaving' | 'soon' | 'error';

export function BookingGate() {
  const { booking } = travelPage.lodging.featured;
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>('checking');
  const [href, setHref] = useState('');
  const [message, setMessage] = useState('');
  const dialog = useRef<HTMLDialogElement>(null);

  /**
   * `showModal` cannot be set declaratively — an `open` attribute makes a
   * non-modal dialog, without the backdrop or the focus trap that are the
   * whole reason for using one.
   */
  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  /**
   * The page behind a modal should not scroll away under it, which `<dialog>`
   * does not handle by itself. Restores whatever was there rather than
   * clearing the property, so this cannot quietly undo someone else's lock.
   */
  useEffect(() => {
    if (!open) return;
    const held = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = held;
    };
  }, [open]);

  /**
   * Asks for the link. Called on opening — a guest who replied earlier still
   * holds a pass and should not be asked anything — and again after a match.
   */
  async function ask(signal?: AbortSignal) {
    setStage('checking');
    try {
      const found = await fetchBooking(signal);
      setHref(found.href);
      setStage('leaving');
      // Straight to the hotel, in this tab. A same-document navigation is not
      // subject to the popup blocking that would swallow a `window.open` this
      // long after the click that opened the dialog — the guest asked to book
      // a room, and the round trip in between is ours, not theirs.
      window.location.assign(found.href);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (error instanceof ApiError && error.status === 401) {
        // No pass, which is the ordinary case rather than a failure. Ask.
        setStage('asking');
        return;
      }
      // 404 is the hotel, not the guest, and it has its own wording here;
      // anything else is repeated in the Worker's words, whatever they were.
      setMessage(error instanceof ApiError ? error.message : '');
      setStage(error instanceof ApiError && error.status === 404 ? 'soon' : 'error');
    }
  }

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    void ask(controller.signal);
    return () => controller.abort();
    // Deliberately only on opening: re-asking is the form's job, not an
    // effect's, or a failed attempt would retry itself forever.
  }, [open]);

  if (!booking.available) {
    return (
      <span class="btn travel-button--disabled" aria-disabled="true">
        {booking.label}
      </span>
    );
  }

  return (
    <>
      <button class="btn" type="button" onClick={() => setOpen(true)}>
        {booking.label}
      </button>

      <dialog
        class="unlock"
        ref={dialog}
        // `aria-labelledby` wins whenever the id resolves, and falls through to
        // the label when it does not — which is the one frame where the dialog
        // is still asking the Worker and has no heading yet.
        aria-label={booking.gate.title}
        aria-labelledby="unlock-title"
        // Escape and the backdrop's own close both come through here, so the
        // component's state cannot drift from the element's.
        onClose={() => setOpen(false)}
        // The dialog element fills the viewport as far as a click is
        // concerned; a click that lands on it rather than on the panel inside
        // is a click on the backdrop.
        onClick={(event) => {
          if (event.target === dialog.current) setOpen(false);
        }}
      >
        <div class="unlock__panel">
          {stage === 'checking' && (
            <div class="unlock__wait" aria-live="polite">
              <p class="notice__wait">One moment…</p>
            </div>
          )}

          {stage === 'asking' && (
            <>
              <span class="eyebrow">{booking.gate.eyebrow}</span>
              <h2 class="unlock__title" id="unlock-title">
                {booking.gate.title}
              </h2>
              <p class="unlock__body">{booking.gate.body}</p>
              <FindForm
                onFound={() => void ask()}
                label={booking.gate.label}
                busyLabel={booking.gate.busyLabel}
              />
            </>
          )}

          {stage === 'leaving' && (
            <>
              <span class="eyebrow">{booking.ready.eyebrow}</span>
              <h2 class="unlock__title" id="unlock-title">
                {booking.ready.title}
              </h2>
              <p class="unlock__body" aria-live="polite">
                {booking.ready.body}
              </p>
              {/* The navigation above is what actually moves them; this is the
                  handhold for the browser that refuses it, and for the moment
                  in between on a slow connection. Not `_blank`: it should do
                  the same thing the redirect does, not a second thing. */}
              <a class="btn btn--onForest unlock__go" href={href} rel="noreferrer">
                {booking.ready.label}
              </a>
            </>
          )}

          {(stage === 'soon' || stage === 'error') && (
            <>
              <span class="eyebrow">{booking.soon.eyebrow}</span>
              <h2 class="unlock__title" id="unlock-title">
                {stage === 'soon' ? booking.soon.title : 'That did not work'}
              </h2>
              <p class="unlock__body" role="alert">
                {stage === 'soon' ? booking.soon.body : message || 'Please try again in a moment.'}
              </p>
            </>
          )}

          {/* One button, two shapes. On a phone the dialog is the whole screen
              and this is the × in the corner; on a wider window it is the
              quiet way out under the form. The label is on the button rather
              than in the text, so it announces the same either way. */}
          <button
            class="unlock__close"
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
          >
            <span class="unlock__closeWord">Close</span>
            <span class="unlock__closeMark" aria-hidden="true">
              ×
            </span>
          </button>
        </div>
      </dialog>
    </>
  );
}
